/**
 * Centralized storage keys configuration with secure naming
 *
 * SECURITY: Using non-predictable, app-specific prefixes to prevent:
 * - Key collisions with other applications
 * - Enumeration attacks that could expose sensitive data
 *
 * Naming convention: il_receipts_v{version}_{feature}_{purpose}
 */

// Version for all storage keys (allows future migrations)
const STORAGE_VERSION = "v1";
const APP_PREFIX = "il_receipts";

/**
 * Helper to build storage key with consistent naming
 */
function buildKey(feature: string, purpose: string): string {
  return `${APP_PREFIX}_${STORAGE_VERSION}_${feature}_${purpose}`;
}

/**
 * All storage keys used across the application
 */
export const STORAGE_KEYS = {
  // Signature service keys (electronic-signature)
  SIGNATURE_KEYPAIR: buildKey("sig", "keypair"),
  SIGNATURE_CERTIFICATE: buildKey("sig", "cert"),

  // Receipt data keys
  RECEIPTS_DATA: buildKey("data", "receipts"),
  RECEIPTS_META: buildKey("data", "meta"),

  // PDF cache keys (sessionStorage)
  PDF_CACHE_HASH: buildKey("pdf", "hash"),
  PDF_CACHE_TIMESTAMP: buildKey("pdf", "ts"),

  // Form state keys
  PDF_FORM_DATA: buildKey("form", "pdf_data"),
  ACCORDION_STATE: buildKey("form", "accordion"),
  METADATA: buildKey("form", "metadata"),

  // Receipt encryption settings
  RECEIPT_ENCRYPTION_ENABLED: buildKey("settings", "enc_enabled"),

  // Document type temporary storage
  DOCUMENT_TYPE: buildKey("temp", "doc_type"),
} as const;

/**
 * Old storage keys for migration purposes
 * Maps old key names to new key constants
 */
export const LEGACY_KEY_MAPPINGS = [
  { old: "ELECTRONIC_SIGNATURE_KEYPAIR", new: STORAGE_KEYS.SIGNATURE_KEYPAIR },
  { old: "BUSINESS_CERTIFICATE", new: STORAGE_KEYS.SIGNATURE_CERTIFICATE },
  { old: "EASY_INVOICE_RECEIPTS", new: STORAGE_KEYS.RECEIPTS_DATA },
  { old: "EASY_INVOICE_RECEIPTS_META", new: STORAGE_KEYS.RECEIPTS_META },
  { old: "pdf_cache_v1", new: STORAGE_KEYS.PDF_CACHE_HASH },
  { old: "pdf_cache_hash_v1", new: STORAGE_KEYS.PDF_CACHE_HASH },
  { old: "pdf_cache_timestamp_v1", new: STORAGE_KEYS.PDF_CACHE_TIMESTAMP },
  { old: "EASY_INVOICE_PDF_DATA", new: STORAGE_KEYS.PDF_FORM_DATA },
  { old: "EASY_INVOICE_ACCORDION_STATE", new: STORAGE_KEYS.ACCORDION_STATE },
  { old: "EASY_INVOICE_METADATA", new: STORAGE_KEYS.METADATA },
  {
    old: "EASY_INVOICE_RECEIPT_ENCRYPTION_ENABLED",
    new: STORAGE_KEYS.RECEIPT_ENCRYPTION_ENABLED,
  },
  { old: "EASY_INVOICE_DOCUMENT_TYPE", new: STORAGE_KEYS.DOCUMENT_TYPE },
] as const;

/**
 * Check if running in browser environment
 */
function isBrowser(): boolean {
  return typeof window !== "undefined";
}

/**
 * Migrate a single key from old to new format
 */
function migrateKey(oldKey: string, newKey: string, storage: Storage): boolean {
  try {
    const oldData = storage.getItem(oldKey);
    if (oldData === null) return false; // No old data to migrate

    // Check if new key already has data (don't overwrite)
    if (storage.getItem(newKey) !== null) {
      // New key exists, just remove old key
      storage.removeItem(oldKey);
      if (process.env.NODE_ENV === "development") {
        console.log(
          `[Storage] Removed legacy key ${oldKey} (new key ${newKey} already exists)`,
        );
      }
      return true;
    }

    // Migrate data
    storage.setItem(newKey, oldData);
    storage.removeItem(oldKey);
    if (process.env.NODE_ENV === "development") {
      console.log(`[Storage] Migrated ${oldKey} → ${newKey}`);
    }
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.error(`[Storage] Failed to migrate ${oldKey}:`, error);
    }
    return false;
  }
}

/**
 * Migrate all legacy storage keys to new secure format
 * Should be called once on application startup
 *
 * @returns Migration statistics
 */
export function migrateStorageKeys(): {
  migrated: number;
  errors: string[];
  localStorageMigrated: number;
  sessionStorageMigrated: number;
} {
  const result = {
    migrated: 0,
    errors: [] as string[],
    localStorageMigrated: 0,
    sessionStorageMigrated: 0,
  };

  if (!isBrowser()) {
    return result;
  }

  if (process.env.NODE_ENV === "development") {
    console.log("[Storage] Starting storage key migration...");
  }

  // Migrate localStorage keys
  for (const { old, new: newKey } of LEGACY_KEY_MAPPINGS) {
    try {
      if (migrateKey(old, newKey, localStorage)) {
        result.migrated++;
        result.localStorageMigrated++;
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Unknown error";
      result.errors.push(`localStorage.${old}: ${errorMsg}`);
    }
  }

  // Migrate sessionStorage keys (for PDF cache)
  const sessionKeys = [
    { old: "pdf_cache_v1", new: STORAGE_KEYS.PDF_CACHE_HASH },
    { old: "pdf_cache_hash_v1", new: STORAGE_KEYS.PDF_CACHE_HASH },
    { old: "pdf_cache_timestamp_v1", new: STORAGE_KEYS.PDF_CACHE_TIMESTAMP },
  ];

  for (const { old, new: newKey } of sessionKeys) {
    try {
      if (migrateKey(old, newKey, sessionStorage)) {
        result.migrated++;
        result.sessionStorageMigrated++;
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Unknown error";
      result.errors.push(`sessionStorage.${old}: ${errorMsg}`);
    }
  }

  if (process.env.NODE_ENV === "development") {
    console.log(
      `[Storage] Migration complete: ${result.migrated} keys migrated (${result.localStorageMigrated} localStorage, ${result.sessionStorageMigrated} sessionStorage)`,
    );

    if (result.errors.length > 0) {
      console.warn("[Storage] Migration errors:", result.errors);
    }
  }

  return result;
}
